/**
 * Payment Service API Client
 * 
 * Handles payment methods (cards, ACH), wallet operations, and subscription payments.
 * Interacts with payment-svc and invoice-svc.
 */

import { apiConfig } from '@/config/apiConfig';
import { getActiveTenantHeaders } from '@/lib/activeTenantScope';
import type {
  // Cards
  GetCardsRequest,
  CardListResponse,
  AddCardRequest,
  AddCardResponse,
  DeleteCardRequest,
  SetDefaultCardRequest,
  SetDefaultResponse,
  DeletePaymentMethodResponse,
  // ACH
  GetACHAccountsRequest,
  ACHListResponse,
  AddACHAccountRequest,
  AddACHResponse,
  DeleteACHAccountRequest,
  SetDefaultACHRequest,
  VerifyACHMicrodepositsRequest,
  VerifyACHResponse,
  CheckVerificationStatusRequest,
  VerificationStatusResponse,
  ProcessACHPaymentRequest,
  ProcessACHPaymentResponse,
  // Payment Methods
  GetPaymentMethodsRequest,
  PaymentMethodsResponse,
  // Wallet
  GetWalletRequest,
  GetWalletResponse,
  BalanceOnlyResponse,
  RechargeWalletRequest,
  RechargeWalletResponse,
  UpdateAutoRechargeRequest,
  UpdateAutoRechargeResponse,
  DebitWalletRequest,
  CreditWalletRequest,
  DebitCreditResponse,
  ActivateWalletRequest,
  DeactivateWalletRequest,
  GenericWalletResponse,
  // Transactions
  GetTransactionsRequest,
  GetTransactionsResponse,
  TransactionCategoriesResponse,
  CategoriesWithAmountsResponse,
  // Insights
  GetInsightsRequest,
  GetInsightsResponse,
  // Subscription Payments
  SetupSubscriptionPaymentRequest,
  ProcessSubscriptionPaymentRequest,
  GetPaymentStatusRequest,
  SyncBillingFromStripeRequest,
  SyncBillingFromStripeResponse,
  SubscriptionPaymentStatusResponse,
  ProcessSubscriptionPaymentResponse,
  // Invoice
  InvoiceSummaryRequest,
  InvoiceSummaryResponse,
  InvoiceShowRequest,
  SubscriptionInvoiceRequest,
  ModuleInvoiceRequest,
  ProrateCalculateRequest,
  ProratedChargeResponse,
  SubscriptionInvoiceData,
  // Module Payment
  ModulePaymentRequest,
  ModulePaymentResponse,
  // Plan Change
  CreateStripeSubscriptionRequest,
  CreateStripeSubscriptionResponse,
  CancelStripeSubscriptionRequest,
  CancelStripeSubscriptionResponse,
  ChangePlanPreviewRequest,
  ChangePlanPreviewResponse,
  ChangePlanRequest,
  ChangePlanResponse,
} from '@/types/payment';

const { baseUrls, paymentEndpoints, invoiceEndpoints } = apiConfig;

/**
 * Custom error class for payment API errors
 */
export class PaymentApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'PaymentApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Fetch wrapper with credentials and error handling
 */
async function fetchWithCredentials<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const activeScopeHeaders = getActiveTenantHeaders();
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...activeScopeHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    throw new PaymentApiError(errorMessage, response.status);
  }

  return response.json();
}

// ============================================
// Payment Methods - Cards
// ============================================

export async function getPaymentMethods(
  data: GetPaymentMethodsRequest
): Promise<PaymentMethodsResponse> {
  return fetchWithCredentials<PaymentMethodsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getPaymentMethods}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getCards(data: GetCardsRequest): Promise<CardListResponse> {
  return fetchWithCredentials<CardListResponse>(
    `${baseUrls.payment}${paymentEndpoints.getCards}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function addCard(data: AddCardRequest): Promise<AddCardResponse> {
  return fetchWithCredentials<AddCardResponse>(
    `${baseUrls.payment}${paymentEndpoints.addCard}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deleteCard(
  data: DeleteCardRequest
): Promise<DeletePaymentMethodResponse> {
  return fetchWithCredentials<DeletePaymentMethodResponse>(
    `${baseUrls.payment}${paymentEndpoints.deleteCard}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function setDefaultCard(
  data: SetDefaultCardRequest
): Promise<SetDefaultResponse> {
  return fetchWithCredentials<SetDefaultResponse>(
    `${baseUrls.payment}${paymentEndpoints.setDefaultCard}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Payment Methods - ACH
// ============================================

export async function getACHAccounts(
  data: GetACHAccountsRequest
): Promise<ACHListResponse> {
  return fetchWithCredentials<ACHListResponse>(
    `${baseUrls.payment}${paymentEndpoints.getACH}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function addACHAccount(
  data: AddACHAccountRequest
): Promise<AddACHResponse> {
  return fetchWithCredentials<AddACHResponse>(
    `${baseUrls.payment}${paymentEndpoints.addACH}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deleteACHAccount(
  data: DeleteACHAccountRequest
): Promise<DeletePaymentMethodResponse> {
  return fetchWithCredentials<DeletePaymentMethodResponse>(
    `${baseUrls.payment}${paymentEndpoints.deleteACH}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function setDefaultACH(
  data: SetDefaultACHRequest
): Promise<SetDefaultResponse> {
  return fetchWithCredentials<SetDefaultResponse>(
    `${baseUrls.payment}${paymentEndpoints.setDefaultACH}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function verifyACHMicrodeposits(
  data: VerifyACHMicrodepositsRequest
): Promise<VerifyACHResponse> {
  return fetchWithCredentials<VerifyACHResponse>(
    `${baseUrls.payment}${paymentEndpoints.verifyACH}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function checkACHVerificationStatus(
  data: CheckVerificationStatusRequest
): Promise<VerificationStatusResponse> {
  return fetchWithCredentials<VerificationStatusResponse>(
    `${baseUrls.payment}${paymentEndpoints.achVerificationStatus}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function processACHPayment(
  data: ProcessACHPaymentRequest
): Promise<ProcessACHPaymentResponse> {
  return fetchWithCredentials<ProcessACHPaymentResponse>(
    `${baseUrls.payment}${paymentEndpoints.processACHPayment}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Wallet Operations
// ============================================

export async function activateWallet(
  data: ActivateWalletRequest
): Promise<GenericWalletResponse> {
  return fetchWithCredentials<GenericWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.activateWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deactivateWallet(
  data: DeactivateWalletRequest
): Promise<GenericWalletResponse> {
  return fetchWithCredentials<GenericWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.deactivateWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getWallet(data: GetWalletRequest): Promise<GetWalletResponse> {
  return fetchWithCredentials<GetWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.getWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getWalletBalance(
  data: GetWalletRequest
): Promise<BalanceOnlyResponse> {
  return fetchWithCredentials<BalanceOnlyResponse>(
    `${baseUrls.payment}${paymentEndpoints.getWalletBalance}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function rechargeWallet(
  data: RechargeWalletRequest
): Promise<RechargeWalletResponse> {
  return fetchWithCredentials<RechargeWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.rechargeWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function updateAutoRecharge(
  data: UpdateAutoRechargeRequest
): Promise<UpdateAutoRechargeResponse> {
  return fetchWithCredentials<UpdateAutoRechargeResponse>(
    `${baseUrls.payment}${paymentEndpoints.updateAutoRecharge}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function debitWallet(
  data: DebitWalletRequest
): Promise<DebitCreditResponse> {
  return fetchWithCredentials<DebitCreditResponse>(
    `${baseUrls.payment}${paymentEndpoints.debitWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function creditWallet(
  data: CreditWalletRequest
): Promise<DebitCreditResponse> {
  return fetchWithCredentials<DebitCreditResponse>(
    `${baseUrls.payment}${paymentEndpoints.creditWallet}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Wallet Transactions
// ============================================

export async function getTransactions(
  data: GetTransactionsRequest
): Promise<GetTransactionsResponse> {
  return fetchWithCredentials<GetTransactionsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getTransactions}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getTransactionCategories(
  data: { userID: number }
): Promise<TransactionCategoriesResponse> {
  return fetchWithCredentials<TransactionCategoriesResponse>(
    `${baseUrls.payment}${paymentEndpoints.getCategories}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getCategoriesWithAmounts(
  data: { userID: number }
): Promise<CategoriesWithAmountsResponse> {
  return fetchWithCredentials<CategoriesWithAmountsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getCategoriesWithAmounts}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Wallet Insights
// ============================================

export async function getInsights(
  data: GetInsightsRequest
): Promise<GetInsightsResponse> {
  return fetchWithCredentials<GetInsightsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getInsights}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Subscription Payments
// ============================================

export async function setupSubscriptionPayment(
  data: SetupSubscriptionPaymentRequest
): Promise<SubscriptionPaymentStatusResponse> {
  return fetchWithCredentials<SubscriptionPaymentStatusResponse>(
    `${baseUrls.payment}${paymentEndpoints.setupSubscriptionPayment}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function processSubscriptionPayment(
  data: ProcessSubscriptionPaymentRequest
): Promise<ProcessSubscriptionPaymentResponse> {
  return fetchWithCredentials<ProcessSubscriptionPaymentResponse>(
    `${baseUrls.payment}${paymentEndpoints.processSubscriptionPayment}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getPaymentStatus(
  data: GetPaymentStatusRequest
): Promise<SubscriptionPaymentStatusResponse> {
  return fetchWithCredentials<SubscriptionPaymentStatusResponse>(
    `${baseUrls.payment}${paymentEndpoints.getPaymentStatus}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

/** Refresh pricing-svc billing dates from Stripe (subscription page load). */
export async function syncBillingFromStripe(
  data: SyncBillingFromStripeRequest
): Promise<SyncBillingFromStripeResponse> {
  return fetchWithCredentials<SyncBillingFromStripeResponse>(
    `${baseUrls.payment}/api/subscription-payment/sync-billing-from-stripe`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Plan Change Operations
// ============================================

/**
 * Create a new Stripe subscription for a user.
 * Call this when a user first subscribes to create a subscription in Stripe.
 */
export async function createStripeSubscription(
  data: CreateStripeSubscriptionRequest
): Promise<CreateStripeSubscriptionResponse> {
  return fetchWithCredentials<CreateStripeSubscriptionResponse>(
    `${baseUrls.payment}/api/subscription-payment/create-subscription`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

/**
 * Cancel Stripe subscription: immediate during trial; at period end when already paying.
 */
export async function cancelStripeSubscription(
  data: CancelStripeSubscriptionRequest
): Promise<CancelStripeSubscriptionResponse> {
  return fetchWithCredentials<CancelStripeSubscriptionResponse>(
    `${baseUrls.payment}/api/subscription-payment/cancel-subscription`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

/**
 * Preview a plan change without executing it.
 * Shows proration for upgrades or scheduling info for downgrades.
 */
export async function previewPlanChange(
  data: ChangePlanPreviewRequest
): Promise<ChangePlanPreviewResponse> {
  return fetchWithCredentials<ChangePlanPreviewResponse>(
    `${baseUrls.payment}/api/subscription-payment/change-plan/preview`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

/**
 * Execute a plan change.
 * - Upgrades: Charges prorated amount immediately
 * - Downgrades: Schedules change for end of billing period
 */
export async function changePlan(
  data: ChangePlanRequest
): Promise<ChangePlanResponse> {
  return fetchWithCredentials<ChangePlanResponse>(
    `${baseUrls.payment}/api/subscription-payment/change-plan`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

// ============================================
// Module Add-on Payment (with proration)
// ============================================

/**
 * Pay for a module add-on with proration support.
 * 
 * 1. Calculates prorated amount based on days remaining in billing period
 * 2. Debits the prorated amount from wallet
 * 3. Attempts invoice generation for the transaction (non-blocking)
 */
export async function payForModuleAddon(
  data: ModulePaymentRequest
): Promise<ModulePaymentResponse> {
  const { userID, module_name, module_fee, prorate = true } = data;

  // Step 1: Calculate proration locally. The subscription page already shows
  // this amount before confirmation, so avoid an extra invoice-svc preview call.
  const localProration = calculateLocalProration(module_fee);
  const now = new Date();
  const prorationResult: ProratedChargeResponse = {
    success: true,
    module_name,
    full_monthly_fee: module_fee,
    prorated_amount: localProration.prorated_amount,
    days_remaining: localProration.days_remaining,
    days_in_month: localProration.days_in_month,
    proration_factor: localProration.proration_factor,
    billing_period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    effective_date: now.toISOString(),
  };
  const chargeAmount = prorate ? prorationResult.prorated_amount : module_fee;
  
  // Step 2: Debit from wallet
  const debitResult = await debitWallet({
    userID,
    amount: chargeAmount,
    category: 'Module Add-on',
    memo: `${module_name} module - ${prorate ? 'Prorated' : 'Full'} charge`,
  });
  
  if (!debitResult.success) {
    throw new PaymentApiError(
      debitResult.message || 'Failed to debit wallet',
      400
    );
  }
  
  const transactionId = debitResult.data?.transactionID as string || undefined;
  let invoiceId: string | undefined;
  let invoiceError: string | undefined;

  // Step 3: Generate invoice. This must not block activation after wallet debit.
  try {
    const invoiceResult = await generateModuleInvoice({
      userID,
      module_name,
      module_fee,
      action: 'enable',
      prorate,
      transaction_id: transactionId,
    });
    invoiceId = invoiceResult.invoiceID;
  } catch (error) {
    invoiceError = error instanceof Error ? error.message : 'Invoice generation failed';
    console.warn('Module add-on invoice generation failed after wallet debit', {
      userID,
      module_name,
      transactionId,
      error,
    });
  }
  
  return {
    success: true,
    status: 'succeeded',
    message: `Successfully charged $${chargeAmount.toFixed(2)} for ${module_name}`,
    prorated_amount: chargeAmount,
    full_amount: module_fee,
    days_remaining: prorationResult.days_remaining,
    days_in_month: prorationResult.days_in_month,
    proration_factor: prorationResult.proration_factor,
    transaction_id: transactionId,
    invoice_id: invoiceId,
    invoice_error: invoiceError,
  };
}

// ============================================
// Invoice Operations
// ============================================

export async function getInvoiceSummary(
  data: InvoiceSummaryRequest
): Promise<InvoiceSummaryResponse> {
  return fetchWithCredentials<InvoiceSummaryResponse>(
    `${baseUrls.invoice}${invoiceEndpoints.getInvoiceSummary}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getInvoice(data: InvoiceShowRequest): Promise<unknown> {
  return fetchWithCredentials<unknown>(
    `${baseUrls.invoice}${invoiceEndpoints.getInvoice}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function getSubscriptionInvoice(
  data: SubscriptionInvoiceRequest
): Promise<SubscriptionInvoiceData> {
  return fetchWithCredentials<SubscriptionInvoiceData>(
    `${baseUrls.invoice}${invoiceEndpoints.getSubscriptionInvoice}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function generateModuleInvoice(
  data: ModuleInvoiceRequest
): Promise<SubscriptionInvoiceData> {
  return fetchWithCredentials<SubscriptionInvoiceData>(
    `${baseUrls.invoice}${invoiceEndpoints.generateModuleInvoice}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function calculateProration(
  data: ProrateCalculateRequest
): Promise<ProratedChargeResponse> {
  return fetchWithCredentials<ProratedChargeResponse>(
    `${baseUrls.invoice}${invoiceEndpoints.calculateProration}`,
    { method: 'POST', body: JSON.stringify(data) }
  );
}

/**
 * Get download URL for invoice PDF
 */
export function getInvoiceDownloadUrl(
  txnId: string
): string {
  return `${baseUrls.invoice}${invoiceEndpoints.downloadInvoice}/${txnId}`;
}

export function getDynamicInvoiceDownloadUrl(
  userId: number,
  category: string,
  txnId: string,
  invoiceId: number | string
): string {
  return `${baseUrls.invoice}${invoiceEndpoints.downloadDynamicInvoice}/${userId}/${encodeURIComponent(category)}/${txnId}/${invoiceId}`;
}

export function getSubscriptionInvoiceDownloadUrl(
  userId: number,
  billingPeriod: string,
  invoiceId: string
): string {
  return `${baseUrls.invoice}${invoiceEndpoints.downloadSubscriptionInvoice}/${userId}/${billingPeriod}/${invoiceId}`;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Check if wallet has sufficient balance for a charge
 */
export async function checkWalletBalance(
  userID: number,
  amount: number
): Promise<{ sufficient: boolean; balance: number; shortfall: number }> {
  const wallet = await getWallet({ userID });
  const balance = wallet.data.availableBalance;
  const sufficient = balance >= amount;
  const shortfall = sufficient ? 0 : amount - balance;
  
  return { sufficient, balance, shortfall };
}

/**
 * Calculate prorated amount for current billing period
 */
export function calculateLocalProration(amount: number): {
  prorated_amount: number;
  days_remaining: number;
  days_in_month: number;
  proration_factor: number;
} {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const currentDay = now.getDate();
  const daysRemaining = daysInMonth - currentDay + 1;
  const prorationFactor = daysRemaining / daysInMonth;
  const proratedAmount = Math.round(amount * prorationFactor * 100) / 100;
  
  return {
    prorated_amount: proratedAmount,
    days_remaining: daysRemaining,
    days_in_month: daysInMonth,
    proration_factor: Math.round(prorationFactor * 10000) / 10000,
  };
}

// ============================================
// Account (Sub-Tenant) Wallet Operations
// ============================================

export interface AccountWalletData {
  id: number;
  account_id: string;
  org_user_id: number;
  balance: number;
  holdAmount: number;
  autoRecharge: number;
  autoRechargeAmount: number;
  autoRechargeTriggerAmount: number;
  status: number;
}

export interface AccountWalletResponse {
  success: boolean;
  data: AccountWalletData;
  message?: string;
}

export interface AccountRechargeRequest {
  account_id: string;
  userID: number;
  amount: number;
  payment_method_id: string;
  /** If set, org owner pays with their own card on behalf of the sub-tenant. */
  charged_by_user_id?: number;
}

export interface AccountAutoRechargeRequest {
  account_id: string;
  userID: number;
  enabled: boolean;
  recharge_amount?: number;
  trigger_amount?: number;
}

export interface AccountTransactionsRequest {
  account_id: string;
  userID: number;
  page?: number;
  page_size?: number;
}

export async function getAccountWallet(data: {
  account_id: string;
  userID: number;
}): Promise<AccountWalletResponse> {
  return fetchWithCredentials<AccountWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.getAccountWallet}`,
    { method: 'POST', body: JSON.stringify({ account_id: data.account_id, org_user_id: data.userID }) }
  );
}

export async function rechargeAccountWallet(
  data: AccountRechargeRequest
): Promise<RechargeWalletResponse> {
  return fetchWithCredentials<RechargeWalletResponse>(
    `${baseUrls.payment}${paymentEndpoints.rechargeAccountWallet}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        rechargeAmount: data.amount,
        methodType: 'card',
        card_id: data.payment_method_id,
        charged_by_user_id: data.charged_by_user_id,
      }),
    }
  );
}

export async function updateAccountAutoRecharge(
  data: AccountAutoRechargeRequest
): Promise<UpdateAutoRechargeResponse> {
  return fetchWithCredentials<UpdateAutoRechargeResponse>(
    `${baseUrls.payment}${paymentEndpoints.updateAccountAutoRecharge}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        autoRecharge: data.enabled ? 1 : 0,
        autoRechargeAmount: data.recharge_amount,
        autoRechargeTriggerAmount: data.trigger_amount,
      }),
    }
  );
}

export async function getAccountTransactions(
  data: AccountTransactionsRequest
): Promise<GetTransactionsResponse> {
  return fetchWithCredentials<GetTransactionsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getAccountTransactions}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        limit: data.page_size,
        page: data.page,
      }),
    }
  );
}

// ============================================
// Account (Sub-Tenant) Payment Methods
// ============================================

export async function getAccountPaymentMethods(data: {
  account_id: string;
  userID: number;
}): Promise<PaymentMethodsResponse> {
  return fetchWithCredentials<PaymentMethodsResponse>(
    `${baseUrls.payment}${paymentEndpoints.getAccountPaymentMethods}`,
    { method: 'POST', body: JSON.stringify({ account_id: data.account_id, org_user_id: data.userID }) }
  );
}

export async function getAccountWalletDashboard(data: {
  account_id: string;
  userID: number;
}): Promise<{
  success: boolean;
  wallet: any;
  transactions: any[];
  payment_methods: { cards: any[]; ach_accounts: any[]; default_card: string | null; default_ach: string | null };
}> {
  return fetchWithCredentials(
    `${baseUrls.payment}${paymentEndpoints.getAccountWalletDashboard}`,
    { method: 'POST', body: JSON.stringify({ account_id: data.account_id, org_user_id: data.userID }) }
  );
}

export async function addAccountCard(data: {
  account_id: string;
  userID: number;
  stripe_payment_method_id: string;
  set_as_default?: boolean;
}): Promise<AddCardResponse> {
  return fetchWithCredentials<AddCardResponse>(
    `${baseUrls.payment}${paymentEndpoints.addAccountCard}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        token: data.stripe_payment_method_id,
        set_as_default: data.set_as_default ?? true,
      }),
    }
  );
}

export async function deleteAccountCard(data: {
  account_id: string;
  userID: number;
  stripe_payment_method_id: string;
}): Promise<DeletePaymentMethodResponse> {
  return fetchWithCredentials<DeletePaymentMethodResponse>(
    `${baseUrls.payment}${paymentEndpoints.deleteAccountCard}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        card_id: data.stripe_payment_method_id,
      }),
    }
  );
}

export async function setDefaultAccountCard(data: {
  account_id: string;
  userID: number;
  stripe_payment_method_id: string;
}): Promise<SetDefaultResponse> {
  return fetchWithCredentials<SetDefaultResponse>(
    `${baseUrls.payment}${paymentEndpoints.setDefaultAccountCard}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        card_id: data.stripe_payment_method_id,
      }),
    }
  );
}

export async function addAccountACH(data: {
  account_id: string;
  userID: number;
  stripe_payment_method_id: string;
}): Promise<AddACHResponse> {
  return fetchWithCredentials<AddACHResponse>(
    `${baseUrls.payment}${paymentEndpoints.addAccountACH}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        token: data.stripe_payment_method_id,
      }),
    }
  );
}

export async function deleteAccountACH(data: {
  account_id: string;
  userID: number;
  stripe_payment_method_id: string;
}): Promise<DeletePaymentMethodResponse> {
  return fetchWithCredentials<DeletePaymentMethodResponse>(
    `${baseUrls.payment}${paymentEndpoints.deleteAccountACH}`,
    {
      method: 'POST',
      body: JSON.stringify({
        account_id: data.account_id,
        org_user_id: data.userID,
        ach_id: data.stripe_payment_method_id,
      }),
    }
  );
}

// ============================================
// API Class Export
// ============================================

export const PaymentAPI = {
  // Cards
  getPaymentMethods,
  getCards,
  addCard,
  deleteCard,
  setDefaultCard,
  // ACH
  getACHAccounts,
  addACHAccount,
  deleteACHAccount,
  setDefaultACH,
  verifyACHMicrodeposits,
  checkACHVerificationStatus,
  processACHPayment,
  // Wallet
  activateWallet,
  deactivateWallet,
  getWallet,
  getWalletBalance,
  rechargeWallet,
  updateAutoRecharge,
  debitWallet,
  creditWallet,
  // Transactions
  getTransactions,
  getTransactionCategories,
  getCategoriesWithAmounts,
  // Insights
  getInsights,
  // Subscription Payments
  setupSubscriptionPayment,
  processSubscriptionPayment,
  getPaymentStatus,
  syncBillingFromStripe,
  // Plan Change
  createStripeSubscription,
  cancelStripeSubscription,
  previewPlanChange,
  changePlan,
  // Module Add-on
  payForModuleAddon,
  // Invoice
  getInvoiceSummary,
  getInvoice,
  getSubscriptionInvoice,
  generateModuleInvoice,
  calculateProration,
  getInvoiceDownloadUrl,
  getDynamicInvoiceDownloadUrl,
  getSubscriptionInvoiceDownloadUrl,
  // Utility
  checkWalletBalance,
  calculateLocalProration,
  // Account Wallet
  getAccountWallet,
  rechargeAccountWallet,
  updateAccountAutoRecharge,
  getAccountTransactions,
  // Account Payment Methods
  getAccountPaymentMethods,
  addAccountCard,
  deleteAccountCard,
  setDefaultAccountCard,
  addAccountACH,
  deleteAccountACH,
};

export default PaymentAPI;


